import google, { mutators, noPrintClass, removeMapControl, repositionMapControl, verify } from "@churchofjesuschrist/maps-google";
import { buttonClass, classNames, controlClass, css, keyframes, proxy, removeUndefined, styleClass } from "@churchofjesuschrist/maps-utils";
import { locationTarget, toHTMLString } from "@churchofjesuschrist/eden-icons-raw";
import MapsService from "@churchofjesuschrist/maps-service";
let initialized = false;
const defaultOptions = {};
class GeolocateControl {
  constructor(options = {}) {
    if (!initialized) {
      verify();
      construct();
      initialized = true;
    }
    this.setOptions({
      ...defaultOptions,
      ...removeUndefined(options)
    });
  }
}
const construct = () => {
  Object.assign(defaultOptions, {
    position: google.maps.ControlPosition.RIGHT_BOTTOM
  });
  const blink = keyframes("blink")`
        to {
            visibility: hidden;
        }
    `;
  const iconSize = "1.5rem";
  const coverClass = styleClass("google-geolocate", "cover")`
        position: absolute;
        top: 50%;
        left: 50%;
        margin-top: calc(${iconSize} / -4);
        margin-left: calc(${iconSize} / -4);
        width: calc(${iconSize} / 2);
        height: calc(${iconSize} / 2);
        background: #ffffff;
        border-radius: 50%;
        z-index: 1;
        pointer-events: none;
        animation: ${blink} 1s steps(5, start) infinite;
    `;
  const stringPromise = MapsService.getString("geolocate");
  Object.setPrototypeOf(GeolocateControl.prototype, Object.assign(new google.maps.MVCObject(), mutators("map"), (() => {
    function onAdd() {
      let map = this.getMap();
      if (this.$map !== map) {
        onRemove.call(this);
        this.$map = map;
        if (window.navigator) {
          if (!this.$div) {
            this.$div = document.createElement("div");
            this.$button = document.createElement("button");
            this.$button.type = "button";
            this.$button.className = buttonClass();
            this.$button.innerHTML = toHTMLString(locationTarget);
            this.$button.addEventListener("click", proxy(onGeolocate, this));
            this.$div.appendChild(this.$button);
            stringPromise.then(string => {
              this.$button.title = string;
            });
          }
          updateClassName.call(this);
          updatePosition.call(this);
          this.$state = {};
          refresh.call(this);
        } else {
          console.error("Geo-locating is not supported on this device.");
        }
      }
    }
    function onRemove() {
      removeMapControl(this.$map, this.$div);
      delete this.$map;
      if (this.$marker) {
        this.$marker.setMap(null);
        delete this.$marker;
      }
    }
    function removeListener() {
      if (this.$listener) {
        google.maps.event.removeListener(this.$listener);
        delete this.$listener;
      }
    }
    function updatePosition() {
      let position = this.get("position");
      repositionMapControl(this.getMap(), this.$div, position);
    }
    function updateClassName() {
      if (this.$div) {
        let className = this.get("className");
        this.$div.className = classNames(className, controlClass(), noPrintClass);
      }
    }
    function refresh() {
      css(this.$button, {
        color: this.$state.viewed ? "#64A53D" : this.$state.error ? "#b00504" : null
      }); // confirmation & danger
      if (this.$state.locating) {
        if (!this.$cover) {
          this.$cover = document.createElement("div");
          this.$cover.className = coverClass;
          this.$div.appendChild(this.$cover);
        }
      } else if (this.$cover) {
        this.$cover.remove();
        delete this.$cover;
      }
    }
    function onGeolocate() {
      let promise = new Promise((resolve, reject) => {
        this.$state.locating = true;
        this.$state.error = false;
        refresh.call(this);
        window.navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true
        });
      });
      google.maps.event.trigger(this, "geolocate", promise);
      promise.then(proxy(onSuccess, this), proxy(onError, this));
      return promise;
    }
    function onSuccess(results) {
      let map = this.getMap();
      if (map) {
        if (!this.$marker) {
          this.$marker = new google.maps.Marker({
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: "#1E9FC8",
              // info
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 1,
              scale: 7
            },
            map
          });
        }
        let position = {
          lat: results.coords.latitude,
          lng: results.coords.longitude
        };
        this.$marker.setPosition(position);
        let circle = new google.maps.Circle({
            center: position,
            radius: results.coords.accuracy
          }),
          bounds = circle.getBounds();
        map.fitBounds(bounds);
        this.$state.viewed = true;
        this.$state.locating = false;
        refresh.call(this);
        removeListener.call(this);
        this.$listener = map.addListener("idle", proxy(onMapIdle, this));
        google.maps.event.trigger(this, "success", results.coords);
      }
    }
    function onMapIdle() {
      let newViewed = false;
      if (this.$marker) {
        newViewed = this.getMap().getBounds().contains(this.$marker.position);
      }
      if (newViewed !== this.$state.viewed) {
        if (!newViewed) {
          removeListener.call(this);
        }
        this.$state.viewed = newViewed;
        refresh.call(this);
      }
    }
    function onError(ex) {
      this.$state.error = true;
      this.$state.locating = false;
      refresh.call(this);
      google.maps.event.trigger(this, "error", ex);
    }
    return {
      getDiv: function () {
        return this.$div;
      },
      geolocate: function () {
        return onGeolocate.call(this);
      },
      changed: function (key) {
        switch (key) {
          case "map":
            if (this.getMap()) {
              onAdd.call(this);
            } else {
              onRemove.call(this);
            }
            break;
          case "position":
            updatePosition.call(this);
            break;
          case "className":
            updateClassName.call(this);
            break;
        }
      }
    };
  })()));
};
export default GeolocateControl;